import { PersonOutlined } from "@mui/icons-material";
import clsx from "clsx";
import { type FC, type KeyboardEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  CheckboxMui,
  CourseIndicatorRides,
  FramerTooltip,
  VehicleOccupancyIconAndColoredLabel,
} from "@ryde/controls";
import {
  CHECKBOX_DEFAULT_SIZE,
  isHighlightedAfterEditSF,
  mapOpenAF,
  OccupancyEnum,
  RidesViewTypeEnum,
  routeColorIdMatchesResolvedSelector,
  routeColorsByIdMapping,
  useFormats,
} from "@ryde/shared";

import {
  formatRideEventDateTime,
  formatRideEventTime,
  getRideStatusColor,
} from "../rides-calendar.helpers";
import {
  directionTooltipKey,
  isCheckboxClick,
  renderDirectionIcon,
} from "./rides-calendar.body-cell.helpers";
import { RIDES_CALENDAR_ENTITY } from "./rides-calendar.constants";
import { type IRidesCalendarRideChipProps } from "./rides-calendar.models";
import { RidesCalendarPassengersTooltip } from "./rides-calendar.passengers-tooltip";

export const RidesCalendarRideChip: FC<IRidesCalendarRideChipProps> = ({
  event,
  timezone,
  isSelected,
  onToggleSelected,
  onOpenRide,
}) => {
  const { t } = useTranslation();
  const { timeFormat } = useFormats();
  const isHighlightedAfterEdit = useRecoilValue(
    isHighlightedAfterEditSF([RIDES_CALENDAR_ENTITY, event.id]),
  );
  const isMapOpen = useRecoilValue(mapOpenAF(RidesViewTypeEnum.Calendar));
  const routeColorIdMatches = useRecoilValue(
    routeColorIdMatchesResolvedSelector,
  );
  const routeColorId = routeColorIdMatches.get(event.id);
  const routeColor =
    isMapOpen && routeColorId
      ? routeColorsByIdMapping[routeColorId]
      : undefined;
  const seatsCount = event.seatsCount ?? event.passengersCount;
  const passengersValue = seatsCount;
  const hasPassengers = passengersValue !== undefined && passengersValue >= 0;
  const passengersInfo = event.passengersInfo ?? [];
  const hasPassengersInfo = passengersInfo.length > 0;
  const hasOccupancy = Boolean(event.occupancy);
  const passengersTooltipContent = hasPassengersInfo ? (
    <RidesCalendarPassengersTooltip passengersInfo={passengersInfo} />
  ) : (
    t("ride.calendar.passengersTooltip", {
      defaultValue: "{{count}} passengers",
      count: seatsCount ?? 0,
    })
  );
  const supplierLabel = event.supplierName;
  const hasCourse = Boolean(event.courseId);
  const fromLabel = event.originStationName;
  const toLabel = event.destinationStationName;
  const hasRoute = Boolean(fromLabel || toLabel);
  const time = formatRideEventTime({ iso: event.start, timeFormat, timezone });

  const handleChipClick = (e: MouseEvent): void => {
    if (isCheckboxClick(e.target)) {
      return;
    }
    onOpenRide(event.id);
  };

  const handleChipKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== "Enter" && e.key !== " ") {
      return;
    }
    if (isCheckboxClick(e.target)) {
      return;
    }
    e.preventDefault();
    onOpenRide(event.id);
  };

  const titleContent = t("ride.calendar.routeTooltip", {
    defaultValue: "#{{id}} - {{from}} → {{to}}",
    id: event.id,
    from: fromLabel ?? "—",
    to: toLabel ?? "—",
  });

  return (
    <div
      className={clsx(
        "rc-chip",
        isSelected && "rc-chip--selected",
        isHighlightedAfterEdit && "rc-chip--highlighted",
      )}
      style={{ borderInlineStartColor: getRideStatusColor(event.status) }}
      onClick={handleChipClick}
      onKeyDown={handleChipKeyDown}
      role="button"
      tabIndex={0}
      title={titleContent}
      data-e2e={`rides-calendar-event-${event.id}`}
    >
      <span
        className="rc-chip__select"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="presentation"
        data-e2e={`rides-calendar-event-select-${event.id}`}
      >
        <CheckboxMui
          checked={isSelected}
          width={CHECKBOX_DEFAULT_SIZE}
          height={CHECKBOX_DEFAULT_SIZE}
          onCheckboxToggle={() => onToggleSelected(event.id)}
        />
      </span>
      <span className="rc-chip__body">
        <span className="rc-chip__line rc-chip__line--head">
          {routeColor && (
            <FramerTooltip
              content={t("ride.calendar.routeColorTooltip", {
                defaultValue: "Route color on map",
              })}
            >
              <span
                className="rc-chip__route-color"
                style={{ backgroundColor: routeColor }}
                data-e2e={`rides-calendar-event-route-color-${event.id}`}
              />
            </FramerTooltip>
          )}
          <FramerTooltip content={t(directionTooltipKey(event.direction))}>
            <span className="rc-chip__direction">
              {renderDirectionIcon(event.direction, event.id)}
            </span>
          </FramerTooltip>
          <FramerTooltip
            content={formatRideEventDateTime({
              iso: event.start,
              timeFormat,
              timezone,
            })}
          >
            <span className="rc-chip__time">{time}</span>
          </FramerTooltip>
          <span
            className="rc-chip__supplier"
            data-e2e={`rides-calendar-event-supplier-${event.id}`}
          >
            <FramerTooltip
              content={t("ride.calendar.supplierTooltip", {
                defaultValue: "Supplier: {{name}}",
                name: supplierLabel,
              })}
            >
              <span className="rc-chip__supplier-text">{supplierLabel}</span>
            </FramerTooltip>
          </span>
          {hasCourse && (
            <span
              className="rc-chip__course"
              data-e2e={`rides-calendar-event-course-${event.id}`}
            >
              <CourseIndicatorRides
                rideDto={{
                  courseId: event.courseId,
                  courseName: event.courseName,
                  courseRouteType: event.courseRouteType,
                }}
              />
            </span>
          )}
          {hasPassengers && (
            <span
              className={clsx(
                "rc-chip__passengers",
                passengersValue === 0 && "rc-chip__passengers--empty",
              )}
              data-e2e={`rides-calendar-event-passengers-${event.id}`}
            >
              <FramerTooltip content={passengersTooltipContent}>
                <span className="rc-chip__passengers-inner">
                  <PersonOutlined
                    className="rc-chip__passengers-icon"
                    sx={{ color: "inherit", fontSize: "inherit" }}
                  />
                  <span className="rc-chip__passengers-count">
                    {passengersValue}
                  </span>
                </span>
              </FramerTooltip>
            </span>
          )}
          {hasOccupancy && (
            <span
              className="rc-chip__occupancy"
              data-e2e={`rides-calendar-event-occupancy-${event.id}`}
            >
              <VehicleOccupancyIconAndColoredLabel
                $busOccupancy={event.occupancy ?? OccupancyEnum.Low}
                vehicleCapacity={event.maxPassengers}
                seatsCount={seatsCount}
                rideStatus={event.status}
              />
            </span>
          )}
        </span>
        {hasRoute && (
          <span className="rc-chip__line rc-chip__line--route">
            <span className="rc-chip__from">{fromLabel ?? "—"}</span>
            <span className="rc-chip__arrow" aria-hidden="true">
              →
            </span>
            <span className="rc-chip__to">{toLabel ?? "—"}</span>
          </span>
        )}
      </span>
    </div>
  );
};
